/**
 * Auth client — mirrors `app/api/v1/auth.py`.
 *
 * Auth lives in an httpOnly cookie set by `/auth/login`. The frontend
 * never sees the JWT itself; it just calls the protected endpoints
 * and trusts the browser to attach the cookie. `/auth/me` is the
 * canonical "am I logged in?" probe — the AuthProvider hits it on
 * mount and uses the result to gate the route tree.
 */

import { api } from "./api";

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  is_superuser: boolean;
}

export interface AuthWorkspace {
  id: string;
  name: string;
  slug: string;
  /** Hex string ("#7c3aed") tinting primary CTAs + active nav.
   *  Null / empty = use the default Meridian palette. */
  accent_color: string | null;
  /** Public URL to the workspace logo. Rendered next to the brand
   *  mark in the navbar. Null = show only the Meridian wordmark. */
  logo_url: string | null;
  /** Workspace-specific AI instructions — prepended to every AI
   *  system prompt. Edited from Administration. */
  ai_instructions: string | null;
  /** Workspace-level AI provider (claude / openai / mock). */
  default_ai_provider?: string;
  /** Provider-namespaced model slug (sonnet / opus / gpt-4o / ...). */
  default_ai_model?: string;
  /** Industry slug (finance / healthcare / etc) — picks the AI's
   *  default vocabulary. Null = generic. */
  industry?: string | null;
  /** Effective product shell after resolving the workspace override against
   *  the environment default. Both shells use the same backend and data. */
  experience_mode?: "classic" | "briefing";
  /** Null means inherit API_DEFAULT_EXPERIENCE for this environment. */
  experience_override?: "classic" | "briefing" | null;
}

export interface FeatureFlags {
  period_calendar_enabled?: boolean;
  period_calendar_range_editing_enabled?: boolean;
  relative_periods_enabled?: boolean;
  version_switchover_enabled?: boolean;
  three_statement_enabled?: boolean;
  companion_proactive_feed_enabled?: boolean;
}

export interface MeResponse {
  is_model_manager?: boolean;
  user: AuthUser;
  workspace: AuthWorkspace;
  /** The caller's role in the active workspace (tenant): owner | admin |
   *  editor | viewer. Drives gating of member management + settings. */
  role: string;
  /** Server feature flags for gating dark UI (Flow 29 etc.). */
  feature_flags?: FeatureFlags;
}

export function login(email: string, password: string): Promise<MeResponse> {
  return api<MeResponse>("/api/v1/auth/login", {
    method: "POST",
    body: { email, password },
  });
}

export interface SignupInput {
  email: string;
  password: string;
  display_name: string;
  /** Optional — backend defaults to "{display_name}'s workspace". */
  workspace_name?: string;
}

/** Self-serve trial creation. Backend creates User + Workspace + OWNER
 *  membership in one transaction and sets the same session cookie as
 *  /login, so the response shape mirrors MeResponse. */
export function signup(input: SignupInput): Promise<MeResponse> {
  return api<MeResponse>("/api/v1/auth/signup", {
    method: "POST",
    body: input,
  });
}

export function logout(): Promise<void> {
  return api<void>("/api/v1/auth/logout", { method: "POST" });
}

export function fetchMe(): Promise<MeResponse> {
  return api<MeResponse>("/api/v1/auth/me");
}

// ---------------------------------------------------------------------------
// Invitation flow — used by SetupPasswordPage.
// ---------------------------------------------------------------------------

export interface InvitationInfo {
  email: string;
  display_name: string | null;
  workspace_name: string | null;
  /** ISO-8601 expiry timestamp. */
  expires_at: string;
}

/** Fetch invitation metadata (email + workspace name) so the
 *  SetupPasswordPage can confirm what the user is accepting before
 *  asking for a password. 404 = invalid OR expired (we don't
 *  distinguish to make enumeration harder). */
export function lookupInvitation(token: string): Promise<InvitationInfo> {
  return api<InvitationInfo>(
    `/api/v1/auth/invitation/${encodeURIComponent(token)}`,
  );
}

/** Set the password for an invited user. Backend hash-compares the
 *  token, sets the password, NULLs the invite columns, and returns
 *  the same MeResponse shape as /login (cookie set as a side effect). */
export function acceptInvite(
  token: string,
  password: string,
): Promise<MeResponse> {
  return api<MeResponse>("/api/v1/auth/accept-invite", {
    method: "POST",
    body: { token, password },
  });
}
